import { TourAvailabilityStatusType } from '@api/models/TourAvailabilityStatusType';

export const MAX_PACKAGE_GUESTS_PER_ROOM = 3;
export const LAST_PERSON_QUOTA_BADGE_MAX = 9;
export const MISSING_OCCUPANCY_PRICE_MESSAGE =
  'Aradığınız kriterlere uygun paket bulunamadı. Lütfen arama kriterlerinizi güncelleyerek tekrar deneyin.';

const hasSellableOccupancyPrice = (price?: number | null): boolean =>
  price != null && price > 0;

export const isMissingOccupancyPrice = (
  adultCount: number,
  childCount: number,
  prices: { price1?: number | null; price3?: number | null }
): boolean => {
  const effectiveAdultCount =
    adultCount === 1 && childCount > 0 ? 2 : adultCount;

  if (effectiveAdultCount <= 1) {
    return !hasSellableOccupancyPrice(prices.price1);
  }
  if (effectiveAdultCount > 2) {
    return !hasSellableOccupancyPrice(prices.price3);
  }
  return false;
};

export interface PackageQuotaState {
  remainingPersonQuota?: number | null;
  isFull?: boolean | null;
  personQuota?: number | null;
  personQuotaUsed?: number | null;
  roomQuota?: number | null;
  roomQuotaUsed?: number | null;
}

export type PackageAvailabilityState = 'available' | 'occupied' | 'callUs';

export type PackageAvailabilityInput = PackageQuotaState & {
  isOccupied?: boolean | null;
  tourAvailabilityStatus?: TourAvailabilityStatusType | string | null;
  tourAvailabilityStatuses?: Array<
    TourAvailabilityStatusType | string | null | undefined
  >;
};

const isPersonQuotaUsedUp = ({
  personQuota,
  personQuotaUsed,
}: PackageQuotaState): boolean =>
  typeof personQuota === 'number' &&
  personQuota > 0 &&
  typeof personQuotaUsed === 'number' &&
  personQuotaUsed >= personQuota;

const isRoomQuotaUsedUp = ({
  roomQuota,
  roomQuotaUsed,
}: PackageQuotaState): boolean =>
  typeof roomQuota === 'number' &&
  roomQuota > 0 &&
  typeof roomQuotaUsed === 'number' &&
  roomQuotaUsed >= roomQuota;

const isRoomQuotaUnavailable = ({
  roomQuota,
  roomQuotaUsed,
}: PackageQuotaState): boolean =>
  roomQuota === 0 || isRoomQuotaUsedUp({ roomQuota, roomQuotaUsed });

const getAvailabilityStatuses = ({
  tourAvailabilityStatus,
  tourAvailabilityStatuses = [],
}: PackageAvailabilityInput) => [
  tourAvailabilityStatus,
  ...tourAvailabilityStatuses,
];

export const resolveRemainingPersonQuota = (
  ...candidates: Array<number | null | undefined>
): number | undefined =>
  candidates.find(
    (value): value is number =>
      typeof value === 'number' && !Number.isNaN(value)
  );

export const showLastPersonQuotaBadge = (
  remainingPersonQuota?: number | null
): boolean =>
  typeof remainingPersonQuota === 'number' &&
  remainingPersonQuota > 0 &&
  remainingPersonQuota <= LAST_PERSON_QUOTA_BADGE_MAX;

export const shouldShowRemainingPersonQuotaBadge = (
  input: PackageAvailabilityInput
): boolean =>
  getPackageAvailabilityState(input) === 'available' &&
  showLastPersonQuotaBadge(input.remainingPersonQuota);

export const isPersonQuotaUnavailable = (
  input: PackageAvailabilityInput
): boolean => {
  if (input.isOccupied === true) return true;
  if (
    getAvailabilityStatuses(input).includes(
      TourAvailabilityStatusType.PERSON_QUOTA_NOT_AVAILABLE
    )
  ) {
    return true;
  }
  if (input.remainingPersonQuota === 0) return true;
  if (typeof input.remainingPersonQuota === 'number') return false;

  return isPersonQuotaUsedUp(input);
};

export const shouldShowLetUsCallYou = (
  input: PackageAvailabilityInput
): boolean => {
  const statuses = getAvailabilityStatuses(input);

  return (
    statuses.includes(TourAvailabilityStatusType.ROOM_QUOTA_NOT_AVAILABLE) ||
    statuses.includes(TourAvailabilityStatusType.PACKAGE_NOT_AVAILABLE) ||
    input.isFull === true ||
    isRoomQuotaUnavailable(input)
  );
};

export const shouldHideCallUsPrice = (
  input: PackageAvailabilityInput
): boolean => {
  const statuses = getAvailabilityStatuses(input);

  return (
    statuses.includes(TourAvailabilityStatusType.ROOM_QUOTA_NOT_AVAILABLE) ||
    input.roomQuota === 0 ||
    isRoomQuotaUsedUp(input)
  );
};

export const isPackageFull = (input: PackageQuotaState): boolean =>
  Boolean(input.isFull || isRoomQuotaUsedUp(input));

export const getPackageAvailabilityState = (
  input: PackageAvailabilityInput
): PackageAvailabilityState => {
  if (isPersonQuotaUnavailable(input)) return 'occupied';
  if (shouldShowLetUsCallYou(input)) return 'callUs';
  return 'available';
};

export const shouldSkipTourPrecalculate = (
  packageMatchesSelection: boolean,
  input: PackageAvailabilityInput
): boolean => {
  if (!packageMatchesSelection) return true;
  if (isPersonQuotaUnavailable(input)) return true;
  if (input.tourAvailabilityStatus === TourAvailabilityStatusType.AVAILABLE) {
    return false;
  }

  return shouldShowLetUsCallYou(input);
};

export const doesLoadedPackageMatchSelection = (
  selectedPackageId: number | null | undefined,
  loaded?: {
    tourPackageId?: number | null;
    defaultPackageDetailId?: number | null;
    hotelDetailIds?: Array<number | null | undefined>;
  }
): boolean => {
  if (selectedPackageId == null || !loaded) return false;

  return (
    loaded.tourPackageId === selectedPackageId ||
    loaded.defaultPackageDetailId === selectedPackageId ||
    Boolean(loaded.hotelDetailIds?.includes(selectedPackageId))
  );
};

export const resolveSelectedHotelDetailId = (
  selectedHotelId: number | null | undefined,
  hotelDetailIds: Array<number | null | undefined>
): number | null =>
  selectedHotelId != null && hotelDetailIds.includes(selectedHotelId)
    ? selectedHotelId
    : null;

export const getEffectivePackageGuestLimit = (
  availablePersonQuota: number | null | undefined
): number =>
  Math.max(
    0,
    Math.min(
      MAX_PACKAGE_GUESTS_PER_ROOM,
      availablePersonQuota ?? MAX_PACKAGE_GUESTS_PER_ROOM
    )
  );
